import { useState, useEffect, useCallback } from 'react';
import { ToastNotification } from '../types';

export const useNotifications = () => {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  const addToast = useCallback((toast: Omit<ToastNotification, 'id' | 'timestamp'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastNotification = {
      ...toast,
      id,
      timestamp: new Date().toISOString(),
      duration: toast.duration || 5000,
      dismissible: toast.dismissible !== false
    };

    setToasts(prev => [...prev, newToast]);

    // Auto dismiss if duration is set
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Notification shortcuts
  const showSuccess = useCallback((title: string, message: string, options?: Partial<ToastNotification>) => {
    addToast({ type: 'success', title, message, ...options });
  }, [addToast]);

  const showError = useCallback((title: string, message: string, options?: Partial<ToastNotification>) => {
    addToast({ type: 'error', title, message, duration: 8000, ...options });
  }, [addToast]);

  const showWarning = useCallback((title: string, message: string, options?: Partial<ToastNotification>) => {
    addToast({ type: 'warning', title, message, ...options });
  }, [addToast]);

  const showInfo = useCallback((title: string, message: string, options?: Partial<ToastNotification>) => {
    addToast({ type: 'info', title, message, ...options });
  }, [addToast]);

  // Generic showNotification function
  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', title?: string) => {
    const notificationTitle = title || type.charAt(0).toUpperCase() + type.slice(1);
    addToast({ type, title: notificationTitle, message });
  }, [addToast]);

  return {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showNotification
  };
};

// Global notification context
let globalNotificationHook: ReturnType<typeof useNotifications> | null = null;

export const setGlobalNotificationHook = (hook: ReturnType<typeof useNotifications>) => {
  globalNotificationHook = hook;
};

export const showGlobalNotification = {
  success: (title: string, message: string, options?: Partial<ToastNotification>) => {
    globalNotificationHook?.showSuccess(title, message, options);
  },
  error: (title: string, message: string, options?: Partial<ToastNotification>) => {
    globalNotificationHook?.showError(title, message, options);
  },
  warning: (title: string, message: string, options?: Partial<ToastNotification>) => {
    globalNotificationHook?.showWarning(title, message, options);
  },
  info: (title: string, message: string, options?: Partial<ToastNotification>) => {
    globalNotificationHook?.showInfo(title, message, options);
  }
};